import React from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/styles";
import ModalityCard from "./ModalityCard";
import { useLocation, useNavigate } from "react-router-dom";
import { useContext } from "react";
import { LanguageContext } from "../../context/langContext";
import useTranslation from "../../hooks/useTranslation";

const useStyles = (theme, isMobile) => ({
  titleContainer: {
    marginTop: isMobile ? "110.4px" : "262.3px",
    display: { md: "flex" },
    justifyContent: { md: "center" },
    alignItems: { md: "center" },
  },
  line: {
    width: "25px",
    border: `1px solid ${theme.palette.red.main}`,
    marginRight: "8px",
    backgroundColor: theme.palette.red.main,
  },
  span: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  arrow: {
    color: theme.palette.grey.main,
    fontSize: "2rem",
    fontWeight: "light",
  },
  container: {
    width: "100%",
    height: "fit-content",
    marginTop: isMobile ? "86px" : "135px",
    borderRadius: "0px 0px 50px 50px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    background: "linear-gradient(to bottom, #081b3e 0%, #26233f 100%)",
    overflow: "hidden",
  },
  sliderContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflowY: "hidden",
  },
  slider: {
    marginTop: isMobile ? "50px" : "110px",
    width: "98%",
    height: "fit-content",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  typography: {
    section: {
      fontSize: isMobile ? "14px" : "16px",
      fontWeight: "bold",
      letterSpacing: "2px",
      color: theme.palette.red.main,
    },
    sectionDescription: {
      fontSize: isMobile ? "32px" : "65px",
      fontFamily: theme.typography.montserrat.bold,
      color: theme.palette.blue.main,
      marginTop: isMobile ? "16px" : "10px",
    },
    container: {
      marginTop: "77px",
      fontSize: "14px",
      letterSpacing: "1.4px",
      fontFamily: theme.typography.sourceSansPro.semibold,
      color: theme.palette.red.main,
    },
  },
  cardsContainer: {
    display: "flex",
    gap: "0px 100px",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  modalityCard: {
    flexGrow: { xs: 1, md: 0, lg: 0, xl: 0 },
  },
});

const Modalities = ({ atrCourse, introCourses, showTitle = true }) => {
  const isMobile = useMediaQuery("(max-width:989px)");
  const theme = useTheme();
  const classes = useStyles(theme, isMobile);
  const navigate = useNavigate();
  const language = useContext(LanguageContext);

  const { pathname } = useLocation();
  const { title, description, modalityCardATR, modalityCardIntroTeacher } =
    useTranslation(pathname, "Modalities");
  const getPriceInRegionalFormat = (number, region) =>
    new Intl.NumberFormat(region, {
      style: "currency",
      currency: region === "es-AR" ? "ARS" : "USD",
      maximumFractionDigits: 0,
      currencyDisplay: "code",
    }).format(parseInt(number));

  return isMobile ? (
    <>
      {showTitle && (
        <>
          <Box sx={classes.titleContainer} id="modalities">
            <Typography sx={classes.typography.section}>{title}</Typography>
          </Box>
          <Typography sx={classes.typography.sectionDescription}>
            {description}
          </Typography>
        </>
      )}
      <Box sx={classes.cardsContainer}>
        <ModalityCard
          title={modalityCardATR.title}
          description={modalityCardATR.description}
          price={
            language === "es"
              ? getPriceInRegionalFormat(atrCourse?.priceARS, "es-AR")
              : getPriceInRegionalFormat(atrCourse?.priceUSD, "en-US")
          }
          installment={
            language === "es"
              ? modalityCardATR.installment.arg
              : modalityCardATR.installment.rdm
          }
          benefits={modalityCardATR.benefits}
          applyButtonText={modalityCardATR.applyButtonText}
          onButtonClick={() =>
            navigate("/aplica/intro/formulario?modalidad=atr")
          }
          sx={classes.modalityCard}
        />
        <ModalityCard
          title={modalityCardIntroTeacher.title}
          description={
            language === "es"
              ? modalityCardIntroTeacher.description.arg
              : modalityCardIntroTeacher.description.rdm
          }
          price={
            language === "es"
              ? introCourses[0] &&
                getPriceInRegionalFormat(introCourses[0]?.priceARS / 6, "es-AR")
              : introCourses[0] &&
                getPriceInRegionalFormat(introCourses[0]?.priceUSD, "en-US")
          }
          installment={
            language === "es"
              ? `${modalityCardIntroTeacher.installment.arg} ${
                  introCourses[0] &&
                  getPriceInRegionalFormat(introCourses[0]?.priceARS, "es-AR")
                }`
              : modalityCardIntroTeacher.installment.rdm
          }
          benefits={modalityCardIntroTeacher.benefits}
          applyButtonText={modalityCardIntroTeacher.applyButtonText}
          onButtonClick={() =>
            navigate("/aplica/intro/fechas?modalidad=videoconferencia")
          }
          sx={classes.modalityCard}
          starred
        />
      </Box>
    </>
  ) : (
    <>
      {showTitle && (
        <>
          <Box sx={classes.titleContainer} id="modalities">
            <div style={classes.line}></div>
            <Typography sx={classes.typography.section}>{title}</Typography>
          </Box>
          <Typography sx={classes.typography.sectionDescription}>
            {description}
          </Typography>
        </>
      )}
      <Box sx={classes.cardsContainer}>
        <ModalityCard
          title={modalityCardATR.title}
          description={modalityCardATR.description}
          price={
            language === "es"
              ? getPriceInRegionalFormat(atrCourse?.priceARS, "es-AR")
              : getPriceInRegionalFormat(atrCourse?.priceUSD, "en-US")
          }
          installment={
            language === "es"
              ? modalityCardATR.installment.arg
              : modalityCardATR.installment.rdm
          }
          benefits={modalityCardATR.benefits}
          applyButtonText={modalityCardATR.applyButtonText}
          onButtonClick={() =>
            navigate("/aplica/intro/formulario?modalidad=atr")
          }
          sx={classes.modalityCard}
        />
        <ModalityCard
          title={modalityCardIntroTeacher.title}
          description={
            language === "es"
              ? modalityCardIntroTeacher.description.arg
              : modalityCardIntroTeacher.description.rdm
          }
          price={
            language === "es"
              ? introCourses[0] &&
                getPriceInRegionalFormat(introCourses[0]?.priceARS / 6, "es-AR")
              : introCourses[0] &&
                getPriceInRegionalFormat(introCourses[0]?.priceUSD, "en-US")
          }
          installment={
            language === "es"
              ? `${modalityCardIntroTeacher.installment.arg} ${
                  introCourses[0] &&
                  getPriceInRegionalFormat(introCourses[0].priceARS, "es-AR")
                }`
              : modalityCardIntroTeacher.installment.rdm
          }
          benefits={modalityCardIntroTeacher.benefits}
          applyButtonText={modalityCardIntroTeacher.applyButtonText}
          onButtonClick={() =>
            navigate("/aplica/intro/fechas?modalidad=videoconferencia")
          }
          sx={classes.modalityCard}
          starred
        />
      </Box>
    </>
  );
};

export default Modalities;
